// Security checks for screenshots people upload (feedback, chat).
//
// 1. Only real PNG, JPEG, WebP or GIF images: the file's first bytes are
//    checked, so a renamed file (script.html -> photo.png) is refused. SVG is
//    never accepted (it can carry scripts).
// 2. Size and count limits.
// 3. Every image is redrawn onto a blank canvas and saved as a fresh PNG/JPEG.
//    That keeps only the pixels: hidden data, scripts appended to the file and
//    photo metadata (GPS location, device) are dropped.
// Storage rules enforce the same types and size on the server.

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const MAX_UPLOAD_FILES = 5;
const MAX_SIDE = 4000; // px; larger images are scaled down

async function sniff(file) {
  const b = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const at = (i, ...v) => v.every((x, k) => b[i + k] === x);
  if (at(0, 0x89, 0x50, 0x4e, 0x47)) return "image/png";
  if (at(0, 0xff, 0xd8, 0xff)) return "image/jpeg";
  if (at(0, 0x47, 0x49, 0x46, 0x38)) return "image/gif";
  if (at(0, 0x52, 0x49, 0x46, 0x46) && at(8, 0x57, 0x45, 0x42, 0x50)) return "image/webp";
  return null;
}

function loadImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("bad image")); };
    img.src = url;
  });
}

/**
 * Checks one file and returns a cleaned copy (a new File), or throws an Error
 * whose message can be shown to the person.
 */
export async function sanitizeImage(file) {
  if (!file || file.size === 0) throw new Error("That file is empty.");
  if (file.size > MAX_UPLOAD_BYTES) throw new Error(`"${file.name}" is over 10 MB.`);
  const real = await sniff(file);
  if (!real) throw new Error(`"${file.name}" is not a PNG, JPEG, WebP or GIF image.`);

  let img;
  try { img = await loadImage(file); } catch { throw new Error(`"${file.name}" could not be read as an image.`); }
  const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  const outType = real === "image/jpeg" ? "image/jpeg" : "image/png";
  if (outType === "image/jpeg") { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h); }
  ctx.drawImage(img, 0, 0, w, h);
  const blob = await new Promise((r) => canvas.toBlob(r, outType, 0.9));
  if (!blob) throw new Error(`"${file.name}" could not be processed.`);
  if (blob.size > MAX_UPLOAD_BYTES) throw new Error(`"${file.name}" is too large after processing.`);
  const base = (file.name || "screenshot").replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9-]/g, "_").slice(0, 60) || "screenshot";
  return new File([blob], `${base}.${outType === "image/jpeg" ? "jpg" : "png"}`, { type: outType });
}
